import { useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AnimatedTitle from "../../../components/AnimatedTitle/AnimatedTitle";
import { useGSAP } from "@gsap/react";
import useUserStore from "../../../store/useUserStore";
import { useImageHeightStore } from "../../../store/useImagesHeight";
import fintechImg from "../../../assets/images/fintech.webp";
import webImg from "../../../assets/images/web-studio.webp";
import mobileImg from "../../../assets/images/mobile-app.webp";

gsap.registerPlugin(ScrollTrigger);

const blocks = [
  {
    img: fintechImg,
    title: "Fintech e‑commerce криптопроекти",
  },
  {
    img: webImg,
    title: {
      mobile: ["Web студії", "аутсорс/аутстаф"],
      desktop: ["Web студії", "аутсорс", "аутстаф"],
    },
  },
  {
    img: mobileImg,
    title: {
      mobile: ["SaaS", "мобільні додатки"],
      desktop: ["SaaS", "мобільні", "додатки"],
    },
  },
];

const containerStyles = {
  width: "100%",
  minHeight: "auto",
  display: "flex",
  justifyContent: "center",
  mt: "165px",
};

const innerContainerStyles = {
  width: "100%",
  maxWidth: "1280px",
};

const gridStyles = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    lg: "1fr 1fr 1fr",
  },
  justifyItems: "center",
  gap: "40px",
  mt: "40px",
};

const blockStyles = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  alignItems: "center",
};

const imageStyles = {
  width: { xs: "320px", sm: "300px" },
  height: { xs: "320px", sm: "300px" },
};

const titleStyles = {
  textAlign: "center",
  fontWeight: 900,
  fontSize: "24px",
  color: "rgba(11, 25, 62, 0.15)",
  textTransform: "uppercase",
  lineHeight: 1.2,
  "@media (min-width: 640px)": { 
    fontSize: "36px",
    lineHeight: 1.1,
  },
};

const TitleComponent = ({ title, isMobile }) => {
  if (typeof title === "object" && title.mobile && title.desktop) {
    const lines = isMobile ? title.mobile : title.desktop;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {lines.map((line, lineIndex) => (
          <Typography key={lineIndex} sx={titleStyles}>
            {line}
          </Typography>
        ))}
      </Box>
    );
  }
  
  if (Array.isArray(title)) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {title.map((line, lineIndex) => (
          <Typography key={lineIndex} sx={titleStyles}>
            {line}
          </Typography>
        ))}
      </Box>
    );
  }
  
  return <Typography sx={titleStyles}>{title}</Typography>;
};

export default function WhoWeWorkWithBlock({ scrollToRef }) {
  const windowInnerWidth = useUserStore((state) => state.windowInnerWidth);
  const imageHeight = useImageHeightStore(
    (state) => state.imageHeights["how-we-work"]
  );
  const imageMobileHeight = useImageHeightStore(
    (state) => state.imageHeights["how-we-work-mobile"]
  );

  const refs = useRef([]);

  const addToRefs = (el) => {
    if (el && !refs.current.includes(el)) {
      refs.current.push(el);
    }
  };

  useGSAP(() => {
    if (windowInnerWidth > 640 && !imageHeight) return;
    if (windowInnerWidth < 640 && !imageMobileHeight) return;

    refs.current.forEach((el, i) => {
      const speed = 1 + i * 0.4;
      gsap.to(el, {
        y: -100 / speed,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      gsap.killTweensOf(refs.current);
      refs.current = [];
    };
  }, [imageHeight, windowInnerWidth, imageMobileHeight]);

  return (
    <Box
      ref={scrollToRef}
      sx={{
        ...containerStyles,
        padding: windowInnerWidth < 768 ? "0 20px" : "0 80px",
      }}
    >
      <Box sx={innerContainerStyles}>
        <AnimatedTitle text="З ким ми працюємо?" sx={{ mb: "91px" }} />
        <Box sx={gridStyles}>
          {blocks.map((item, index) => (
            <Box key={index} sx={blockStyles}>
              <Box
                component="img"
                src={item.img}
                ref={addToRefs}
                alt={`image ${index}`}
                sx={imageStyles}
              />
              <TitleComponent title={item.title} isMobile={windowInnerWidth < 768} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
